/**
 * Ad Engine document schema (`vm_Video.adProjectJson`).
 *
 * One JSON document per ad holds every stage artifact plus its prompt traces. Stages
 * are functions `(doc, deps) → doc'`; this file owns the shape they agree on — enums,
 * interfaces, zod parsers, the three fixed model ids and the numeric policy constants.
 *
 * Spec: claude/specs/vidmax-ad-engine.spec.md §3.5.
 */

import { z } from "zod"
import { zShotPlan, zShotRun } from "../adEngine/shotRecipes/schema"
import type { IShotPlan, IShotRun } from "../adEngine/shotRecipes/schema"

import {
  ELipsyncModel,
  GoogleImageGenerationModelType,
  KlingAIVideoGenerationModelType,
  PrunaAIImageGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "@aix/apiutils/src/image/enums"

import type { TeamId, VideoId } from "../types/branded"

// ───────────────────────────── enums ─────────────────────────────

export enum EAdStage {
  SCRIPT = "script",
  SPEECH = "speech",
  SHOTS = "shots",
  KEYFRAME = "keyframe",
  COMPILE = "compile",
  GENERATE = "generate",
  TIMELINE = "timeline",
  RENDER = "render",
}

export enum EAdMouth {
  ON_CAMERA = "on_camera",
  NONE = "none",
}

export enum EAdFraming {
  MCU = "mcu",
  CU = "cu",
  INSERT = "insert",
  SCREEN = "screen",
}

/** How the timeline paces the voice: `tight` cuts to the words and jump-cuts pauses; `natural` keeps the file as recorded. */
export enum EAdPacing {
  TIGHT = "tight",
  NATURAL = "natural",
}

/** Opt-in provider smoke profile; absent preserves the production model choices. */
export enum EAdGenerationProfile {
  STANDARD = "standard",
  CHEAP_CANARY = "cheap_canary",
}

export enum EAdRecipe {
  PRESENTER_LIPSYNC = "presenter_lipsync",
  I2V_SILENT_VO = "i2v_silent_vo",
  I2V_SILENT = "i2v_silent",
}

export enum EAdFreshness {
  FRESH = "fresh",
  STALE = "stale",
  EMPTY = "empty",
}

export enum EAdSlotKind {
  AVATAR = "avatar",
  VOICE = "voice",
  ENVIRONMENT = "environment",
  PRODUCT = "product",
}

/**
 * The six error codes + `E_VALIDATION` (spec D19). Modelled as an `E*` enum rather than a
 * bare string union because the repo standard is TS enums for closed sets; the *values* are
 * exactly the strings in the §3.5 Names table, which is what tests and clients assert on.
 */
export enum EAdErrorCode {
  E_DOC_CONFLICT = "E_DOC_CONFLICT",
  E_CAP_EXCEEDED = "E_CAP_EXCEEDED",
  E_GATE_KEYFRAMES = "E_GATE_KEYFRAMES",
  E_TAKE_TOO_SHORT = "E_TAKE_TOO_SHORT",
  E_SHOT_SPLIT_REQUIRED = "E_SHOT_SPLIT_REQUIRED",
  E_VALIDATION = "E_VALIDATION",
  E_RECIPE_INPUT = "E_RECIPE_INPUT",
  E_METHOD_INCOMPATIBLE = "E_METHOD_INCOMPATIBLE",
  E_PLAN_INVALID = "E_PLAN_INVALID",
  E_BUDGET = "E_BUDGET",
  E_SUBMISSION_UNKNOWN = "E_SUBMISSION_UNKNOWN",
  E_BASELINE_CONFLICT = "E_BASELINE_CONFLICT",
}

/** `details[].rule` values carried by `E_VALIDATION` (and the two rule-shaped errors). */
export enum EAdRule {
  FORMAT_NOT_FOUND = "format_not_found",
  ALREADY_INITIALIZED = "already_initialized",
  BINDING_KIND = "binding_kind",
  BINDING_REQUIRED = "binding_required",
  SCRIPT_ROLE = "script_role",
  SCRIPT_WORD_BUDGET = "script_word_budget",
  SHOT_FRAMING = "shot_framing",
  MOUTH_NO_SPEECH = "mouth_no_speech",
  LINE_COVERAGE = "line_coverage",
  SHOT_ASSET_ROLE = "shot_asset_role",
  TAKE_NOT_SETTLED = "take_not_settled",
  /** Two silent inserts about the same subject — the "pouch beside the glass three times" edit. */
  SHOT_SUBJECT_REPEAT = "shot_subject_repeat",
  /** Three consecutive lines covered by the identical shot shape — the formula, not an edit. */
  SHOT_SHAPE_MONOTONE = "shot_shape_monotone",
}

/** Realism verdict on one take or the final render (QA spec I1). ERROR = the judge could not grade. */
export enum EAdQaVerdict {
  PASS = "pass",
  MINOR = "minor",
  FAIL = "fail",
  ERROR = "error",
}

/** What the grade was taken over — picks the rubric template. */
export enum EAdQaKind {
  KEYFRAME = "keyframe",
  CLIP = "clip",
  RENDER = "render",
}

/** Tell → fix recipe, ids match the R1–R8 catalog the rubric was ported from. */
export enum EAdQaRecipe {
  R1_SCREEN_TEXT = "r1",
  R2_LABEL_TEXT = "r2",
  R3_HANDS = "r3",
  R4_WAXY_SKIN = "r4",
  R5_STAGED = "r5",
  R6_IDENTITY_MORPH = "r6",
  R7_LIPSYNC = "r7",
  R8_CAMERA_HUD = "r8",
  // Persisted in ad docs and parsed strictly by every deployed reader: a new value here makes
  // older API builds 500 on any doc that carries it. Add a field instead (see `productMatch`).
  OTHER = "other",
}

/** Keyframe vs the bound product packshot (VM-1179). Only asked when the shot shows the product. */
export enum EAdProductMatch {
  MATCH = "match",
  MISMATCH = "mismatch",
  /** The judge cannot tell — too small, turned away, occluded. A human looks. */
  UNCLEAR = "unclear",
  NOT_SHOWN = "not_shown",
}

/** Where a grade lands for the human: decided by `reviewOf`, never by the judge (VM-1179). */
export enum EAdReviewBucket {
  AUTO_PASS = "auto_pass",
  AUTO_FAIL = "auto_fail",
  NEEDS_REVIEW = "needs_review",
}

/** Why a grade was not trusted on its own. */
export enum EAdReviewReason {
  JUDGE_ERROR = "judge_error",
  NEAR_THRESHOLD = "near_threshold",
  /** The cheap judge failed it and the confirm judge passed it. */
  JUDGES_DISAGREE = "judges_disagree",
  /** The cheap judge failed it and the confirm judge could not run. */
  CONFIRM_UNAVAILABLE = "confirm_unavailable",
  PRODUCT_UNCLEAR = "product_unclear",
}

/** Which half of the review queue to list. */
export enum EAdReviewFilter {
  /** Flagged and not yet answered — the default work list. */
  OPEN = "open",
  /** Flagged and answered — to audit or change an answer. */
  ANSWERED = "answered",
}

/** A human's call on a graded take — calibration ground truth. */
export enum EAdHumanVerdict {
  PASS = "pass",
  FAIL = "fail",
}

/** What the whole-ad judge scores (VM-1174). Realism QA grades media; this grades the ad as an ad. */
export enum EAdJudgeDimension {
  HOOK = "hook",
  MESSAGE = "message",
  PRODUCT = "product",
  REALISM = "realism",
  CONSISTENCY = "consistency",
  PACING = "pacing",
  CTA = "cta",
}

// ───────────────────────────── constants ─────────────────────────────

/** v1 models are constants, not a registry (D5). */
export const AD_ENGINE_MODELS = {
  video: KlingAIVideoGenerationModelType.KLING_3_0,
  lipsync: ELipsyncModel.AURORA,
  image: GoogleImageGenerationModelType.NANO_BANANA_2,
} as const

export const AD_ENGINE_CANARY_MODELS = {
  image: PrunaAIImageGenerationModelType.Z_IMAGE_TURBO,
  video: WavespeedVideoGenerationModelType.WAN_2_2_5B_FAST,
} as const

export const AD_ENGINE_CONSTANTS = {
  /** Extra video generated past speech end; trimmed off in the timeline (D20). */
  GEN_TAIL_PAD_SEC: 0.4,
  /** Silence between lines in the timeline; part of a shot's duration (D20). */
  CUT_GAP_SEC: 0.15,
  MAX_LIPSYNC_SEC: 20,
  MAX_CLIP_SEC: 10,
  KLING_MIN_SEC: 3,
  KLING_MAX_SEC: 15,
  KLING_MAX_SHOTS_PER_CLIP: 6,
  DEFAULT_COST_CAP_USD: 8,
  /** No cut shorter than this — a sub-second flash reads as a glitch, not an edit. */
  MIN_SHOT_SEC: 1.4,
  /** Floor for an on-camera cut inside a line; lipsync bills the whole line anyway, so this is about readability. */
  MIN_ON_CAMERA_SEC: 2.0,
  /** Floor for a silent insert inside a line: under this it still costs KLING_MIN_SEC of generation for a flash. */
  MIN_INSERT_SEC: 2.5,
  /** `IAdShot.subject` cap; the grammar clamps to it so a wordy model can never write an unreadable doc. */
  MAX_SUBJECT_CHARS: 40,
  /** Audio kept after a line's last word when pacing trims to the words (pacing spec). */
  SPEECH_TAIL_SEC: 0.08,
  /** Upper bound on `IAdShot.weight`; wider ratios starve the other shots in the group. */
  MAX_SHOT_WEIGHT: 3,
} as const

/** QA gate policy (QA spec §3.5). */
export const AD_QA_CONSTANTS = {
  /** Below this holistic score a take fails even with no named tell. */
  PASS_SCORE: 7,
  /** Autopilot rerolls a unit at most this many times for QA before stopping. */
  MAX_QA_REROLLS: 2,
  CLIP_FRAMES: 4,
  RENDER_FRAMES: 8,
  /** Parallel judge calls per grading pass. */
  CONCURRENCY: 4,
  /** Sampled frames are scaled to this width before they reach the judge. */
  FRAME_WIDTH: 720,
  /**
   * A score-only FAIL within this distance under `PASS_SCORE` goes to human review. Passes are not
   * banded: Gemini scores most passing keyframes exactly 7.0, so a symmetric band flagged 12 of 21
   * on the first live run (VM-1179) — a queue nobody would clear.
   */
  REVIEW_BAND: 1,
} as const

/**
 * Whole-ad judge policy (VM-1174). `overall` is the weighted mean of dimension scores, computed by
 * us. Calibrated 2026-10-05: proven winners (raw testimonials) score 4-6 on product/CTA, so craft
 * dimensions only fail when catastrophic; realism + consistency are the hard gates.
 */
export const AD_JUDGE_CONSTANTS = {
  /** Weighted overall below this fails. Proven winners landed 5.3-6.5, our renders 4.8-5.0. */
  PASS_OVERALL: 5.2,
  /** A dimension below its floor fails the ad whatever the overall. */
  FLOORS: {
    [EAdJudgeDimension.HOOK]: 3,
    [EAdJudgeDimension.MESSAGE]: 3,
    [EAdJudgeDimension.PRODUCT]: 3,
    [EAdJudgeDimension.REALISM]: 5,
    [EAdJudgeDimension.CONSISTENCY]: 5,
    [EAdJudgeDimension.PACING]: 3,
    [EAdJudgeDimension.CTA]: 3,
  } satisfies Record<EAdJudgeDimension, number>,
  /** Passing ads with any dimension under this are MINOR — runnable, with a named weakness. */
  MINOR_BELOW: 6,
  /** Dense frames over the first 3s: the hook decides whether anyone sees the rest. */
  HOOK_FRAME_SEC: [0.3, 1.2, 2.4],
  /** Frames spread evenly over the rest of the cut. */
  BODY_FRAMES: 9,
  /** ffmpeg scene-change score that counts as a cut. */
  CUT_SCENE_THRESHOLD: 0.3,
  WEIGHTS: {
    [EAdJudgeDimension.HOOK]: 0.25,
    [EAdJudgeDimension.MESSAGE]: 0.15,
    [EAdJudgeDimension.PRODUCT]: 0.15,
    [EAdJudgeDimension.REALISM]: 0.15,
    [EAdJudgeDimension.CONSISTENCY]: 0.1,
    [EAdJudgeDimension.PACING]: 0.1,
    [EAdJudgeDimension.CTA]: 0.1,
  } satisfies Record<EAdJudgeDimension, number>,
} as const

export const AD_PROJECT_SCHEMA_VERSION = "1.0.0"

// ───────────────────────────── errors ─────────────────────────────

export interface IAdErrorDetail {
  rule: EAdRule
  shotId?: string
  lineId?: string
  message: string
}

export interface IAdEngineError extends Error {
  adEngineCode: EAdErrorCode
  details?: IAdErrorDetail[]
}

/**
 * Pure modules can't build a `TRPCError` without dragging the server dep into the shared
 * package, so they throw a tagged `Error`; the API boundary maps `adEngineCode` onto the
 * TRPC code (see `service/adEngine/errors.ts`).
 */
export const adEngineError = (code: EAdErrorCode, message: string, details?: IAdErrorDetail[]): IAdEngineError => {
  const error = new Error(message) as IAdEngineError
  error.adEngineCode = code
  if (details) error.details = details
  return error
}

export const isAdEngineError = (error: unknown): error is IAdEngineError =>
  error instanceof Error && typeof (error as IAdEngineError).adEngineCode === "string"

// ───────────────────────────── format types ─────────────────────────────

export interface IAdPattern {
  id: string
  name: string
  /** One sentence an operator can read to know when to reach for this spine. */
  summary: string
  beats: Array<{
    id: string
    kind: string
    job: string
    timingRange: { minSec: number; maxSec: number }
    wordBudget: number
  }>
}

export interface IAdConcept {
  id: string
  name: string
  /** One sentence on how this concept is shot — the half of the format that owns the camera. */
  summary: string
  roles: Array<{ slotId: string; kind: EAdSlotKind; role: string; required: boolean }>
  setting: string
  blocking: EAdFraming[]
  grammar: { allowedFramings: EAdFraming[] }
}

export interface IAdFormat {
  id: string
  pattern: IAdPattern
  concept: IAdConcept
}

// ───────────────────────────── doc types ─────────────────────────────

export interface IPromptTrace {
  templateId: string
  version: number
  model: string
  resolvedPrompt: string
  override?: string
  raw: string
  parsed: unknown
  /** Set when the raw response failed the stage schema; `parsed` then holds the pre-validation JSON. */
  parseError?: string
  createdAt: string
}

export interface IAdScriptLine {
  id: string
  beatId: string
  roleSlotId: string
  text: string
  intent: string
}

export interface IAdSpeechWord {
  w: string
  start: number
  end: number
}

export interface IAdSpeechLine {
  usd?: number
  lineId: string
  jobId: string
  status: "pending" | "completed" | "failed"
  audioKey?: string
  durationSec?: number
  words?: IAdSpeechWord[]
}

/**
 * A line is covered by one *or more* consecutive shots: at most one on-camera lipsync shot
 * (always first, so Aurora's video starts in sync with the line's audio) followed by silent
 * voiceover inserts. The line's measured speech still fixes the group's total length — the
 * director only chooses how that total is *divided*, via `weight`.
 */
export interface IAdShot {
  id: string
  beatId: string
  framing: EAdFraming
  mouth: EAdMouth
  lineId?: string
  prompt: string
  motionPrompt: string
  productAssetRole?: string
  /** Relative share of the line's runtime among the shots covering it (1-3, default 1). */
  weight?: number
  /** The one noun the shot is about ("fridge door", "pouch"); inserts must not repeat one. */
  subject?: string
  durationSec: number
}

export interface IAdQaTell {
  recipe: EAdQaRecipe
  /** Concrete visual evidence. */
  note: string
  /** A clear AI tell — fails the take whatever the score. */
  hard: boolean
  atSec?: number
}

/** One realism grade. `verdict` is derived from `tells` + `score` by `verdictOf`, never by the judge. */
export interface IAdQaGrade {
  verdict: EAdQaVerdict
  score: number
  tells: IAdQaTell[]
  summary: string
  kind: EAdQaKind
  templateId: string
  version: number
  model: string
  /** Judge cost. Outside `doc.spend` — LLM calls are not provider spend (QA spec D8). */
  usd: number
  gradedAt: string
  error?: string
  /** Set when the cheap judge said FAIL and the confirm judge produced this grade (QA spec D13). */
  triage?: { model: string; verdict: EAdQaVerdict; score: number }
  /** Set only when the bound product's packshot was compared (keyframe product shots). */
  productMatch?: EAdProductMatch
  review?: IAdQaReview
  human?: IAdHumanReview
}

export interface IAdQaReview {
  bucket: EAdReviewBucket
  reasons: EAdReviewReason[]
}

export interface IAdHumanReview {
  verdict: EAdHumanVerdict
  note?: string
  /** vm_User id of the reviewer. */
  by: string
  at: string
}

export interface IAdTake {
  takeIndex: number
  jobId: string
  modelId: string
  status: "pending" | "completed" | "failed"
  assetId?: string
  url?: string
  durationSec?: number
  usd?: number
  qa?: IAdQaGrade
}

export interface IAdClip {
  id: string
  shotIds: string[]
  recipe: EAdRecipe
  modelId: string
  params: Record<string, unknown>
  estimatedUsd: number
  reason: string
}

export interface IAdPlan {
  clips: IAdClip[]
  totalUsd: number
  gateHash: string
  /** Seconds of silent generation past the content it covers (Kling floor + tail pad); absent on pre-v5 plans. */
  wasteSec?: number
}

export interface IAdBinding {
  kind: EAdSlotKind
  id?: string
  text?: string
}

export interface IAdContext {
  product: { name: string; oneLiner: string; features: string[]; offer?: string }
  brand: { name: string; voice?: string }
  notes: string[]
}

export interface IAdStageState {
  inputHash?: string
  lastRunAt?: string
  lastError?: string
}

/** Durable intent created before dispatch; batchId on vm_AssetJob is this id. */
export interface IAdSubmission {
  id: string
  executionId?: string
  stage: EAdStage
  unitId: string
  modelId: string
  createdAt: string
  estimatedUsd?: number
  jobId?: string
  settled?: boolean
  target?: { kind: "shot"; runId: string; stepId: string }
}

export interface IAdExecution {
  id: string
  stage: EAdStage
  inputHash: string
  startedAt: string
  target?: { kind: "shot"; runId: string; stepId: string }
}

/** The one Remotion render job RENDER dispatched; settled by `pollStage(render)` like a take. */
export interface IAdRender {
  jobId: string
  status: "pending" | "completed" | "failed"
  url?: string
  error?: string
  dispatchedAt: string
  qa?: IAdQaGrade
  /** Whole-ad grade (hook, message, pacing, CTA …), set by `adEngine.judge`. */
  judge?: IAdJudgeGrade
}

export interface IAdJudgeScore {
  dimension: EAdJudgeDimension
  score: number
  evidence: string
}

/** A flaw that fails the ad whatever the scores — the judge names it, `adVerdictOf` enforces it. */
export interface IAdJudgeBlocker {
  dimension: EAdJudgeDimension
  note: string
  atSec?: number
}

/** Measured by ffmpeg, not the judge — pacing facts the model cannot hallucinate. */
export interface IAdCutStats {
  durationSec: number
  cuts: number
  avgShotSec: number
  /** When the first cut lands; past ~3s the hook is one static shot. */
  firstCutSec?: number
}

/** One whole-ad grade. `verdict` and `overall` are derived (`adVerdictOf`), never asked of the model. */
export interface IAdJudgeGrade {
  verdict: EAdQaVerdict
  overall: number
  scores: IAdJudgeScore[]
  blockers: IAdJudgeBlocker[]
  /** Concrete revisions, highest leverage first — what the engine loop feeds back. */
  fixes: string[]
  summary: string
  cutStats?: IAdCutStats
  templateId: string
  version: number
  model: string
  usd: number
  gradedAt: string
  error?: string
}

export interface IAdProject {
  schemaVersion: "1.0.0"
  docVersion: number
  formatId: string
  bindings: Record<string, IAdBinding>
  context: IAdContext
  costCapUsd: number
  generationProfile?: EAdGenerationProfile
  /** Absent reads as `EAdPacing.TIGHT` (pacing spec D1). */
  pacing?: EAdPacing
  script?: { lines: IAdScriptLine[] }
  speech?: { lines: Record<string, IAdSpeechLine> }
  shots?: IAdShot[]
  keyframes?: {
    anchorTakeId?: string
    takes: Record<string, IAdTake[]>
    selected: Record<string, number>
    approved?: { at: string; byUserId: string; hash: string }
  }
  plan?: IAdPlan
  video?: { takes: Record<string, IAdTake[]>; selected: Record<string, number> }
  traces: Partial<Record<EAdStage, IPromptTrace[]>>
  overrides: Partial<Record<EAdStage, string>>
  stages: Partial<Record<EAdStage, IAdStageState>>
  spend: { usd: number }
  execution?: IAdExecution
  submissions?: IAdSubmission[]
  recipePlans?: Record<string, IShotPlan>
  /** Durable shot recipe attempts; absent in projects created before the recipe catalog. */
  recipeRuns?: Record<string, IShotRun>
  /** Set by RENDER; `pollStage(render)` settles it to the finished mp4 (or the Lambda error). */
  render?: IAdRender
  /** Set by `adEngine.clone`: where this doc was forked from, and at which docVersion. */
  clonedFrom?: { videoId: string; docVersion: number; at: string }
}

// ───────────────────────────── zod ─────────────────────────────

export const zAdStage = z.nativeEnum(EAdStage)
export const zAdSlotKind = z.nativeEnum(EAdSlotKind)

export const zAdBinding = z.object({
  kind: zAdSlotKind,
  id: z.string().min(1).optional(),
  text: z.string().min(1).optional(),
})

export const zAdContext = z.object({
  product: z.object({
    name: z.string(),
    oneLiner: z.string(),
    features: z.array(z.string()),
    offer: z.string().optional(),
  }),
  brand: z.object({ name: z.string(), voice: z.string().optional() }),
  notes: z.array(z.string()),
})

export const zAdScriptLine = z.object({
  id: z.string().min(1),
  beatId: z.string().min(1),
  roleSlotId: z.string().min(1),
  text: z.string().min(1),
  intent: z.string(),
})

export const zAdSpeechLine = z.object({
  usd: z.number().nonnegative().optional(),
  lineId: z.string().min(1),
  jobId: z.string().min(1),
  status: z.enum(["pending", "completed", "failed"]),
  audioKey: z.string().optional(),
  durationSec: z.number().nonnegative().optional(),
  words: z.array(z.object({ w: z.string(), start: z.number(), end: z.number() })).optional(),
})

export const zAdShot = z.object({
  id: z.string().min(1),
  beatId: z.string().min(1),
  framing: z.nativeEnum(EAdFraming),
  mouth: z.nativeEnum(EAdMouth),
  lineId: z.string().min(1).optional(),
  prompt: z.string(),
  motionPrompt: z.string(),
  productAssetRole: z.string().optional(),
  weight: z.number().int().min(1).max(AD_ENGINE_CONSTANTS.MAX_SHOT_WEIGHT).optional(),
  subject: z.string().min(1).max(AD_ENGINE_CONSTANTS.MAX_SUBJECT_CHARS).optional(),
  durationSec: z.number().nonnegative(),
})

export const zAdQaTell = z.object({
  // Tolerant read: a recipe id this build does not know (written by a newer build) must not 500
  // every read of the doc — `adEngine.get` on dev broke exactly that way (VM-1179).
  recipe: z.nativeEnum(EAdQaRecipe).catch(EAdQaRecipe.OTHER),
  note: z.string(),
  hard: z.boolean(),
  atSec: z.number().nonnegative().optional(),
})

export const zAdQaGrade = z.object({
  verdict: z.nativeEnum(EAdQaVerdict),
  score: z.number().min(0).max(10),
  tells: z.array(zAdQaTell),
  summary: z.string(),
  kind: z.nativeEnum(EAdQaKind),
  templateId: z.string(),
  version: z.number().int(),
  model: z.string(),
  usd: z.number().nonnegative(),
  gradedAt: z.string(),
  error: z.string().optional(),
  triage: z.object({ model: z.string(), verdict: z.nativeEnum(EAdQaVerdict), score: z.number() }).optional(),
  productMatch: z.nativeEnum(EAdProductMatch).optional(),
  review: z
    .object({ bucket: z.nativeEnum(EAdReviewBucket), reasons: z.array(z.nativeEnum(EAdReviewReason)) })
    .optional(),
  human: z
    .object({
      verdict: z.nativeEnum(EAdHumanVerdict),
      note: z.string().optional(),
      by: z.string(),
      at: z.string(),
    })
    .optional(),
})

export const zAdJudgeGrade = z.object({
  verdict: z.nativeEnum(EAdQaVerdict),
  overall: z.number().min(0).max(10),
  scores: z.array(
    z.object({ dimension: z.nativeEnum(EAdJudgeDimension), score: z.number().min(0).max(10), evidence: z.string() }),
  ),
  blockers: z.array(
    z.object({
      dimension: z.nativeEnum(EAdJudgeDimension),
      note: z.string(),
      atSec: z.number().nonnegative().optional(),
    }),
  ),
  fixes: z.array(z.string()),
  summary: z.string(),
  cutStats: z
    .object({
      durationSec: z.number().nonnegative(),
      cuts: z.number().int().nonnegative(),
      avgShotSec: z.number().nonnegative(),
      firstCutSec: z.number().nonnegative().optional(),
    })
    .optional(),
  templateId: z.string(),
  version: z.number().int(),
  model: z.string(),
  usd: z.number().nonnegative(),
  gradedAt: z.string(),
  error: z.string().optional(),
})

export const zAdTake = z.object({
  takeIndex: z.number().int().nonnegative(),
  jobId: z.string().min(1),
  modelId: z.string().min(1),
  status: z.enum(["pending", "completed", "failed"]),
  assetId: z.string().optional(),
  url: z.string().optional(),
  durationSec: z.number().nonnegative().optional(),
  usd: z.number().nonnegative().optional(),
  qa: zAdQaGrade.optional(),
})

export const zAdClip = z.object({
  id: z.string().min(1),
  shotIds: z.array(z.string().min(1)).min(1),
  recipe: z.nativeEnum(EAdRecipe),
  modelId: z.string().min(1),
  params: z.record(z.string(), z.unknown()),
  estimatedUsd: z.number().nonnegative(),
  reason: z.string(),
})

export const zAdPlan = z.object({
  clips: z.array(zAdClip),
  totalUsd: z.number().nonnegative(),
  gateHash: z.string(),
  wasteSec: z.number().nonnegative().optional(),
})

export const zPromptTrace = z.object({
  templateId: z.string(),
  version: z.number().int(),
  model: z.string(),
  resolvedPrompt: z.string(),
  override: z.string().optional(),
  raw: z.string(),
  parsed: z.unknown(),
  parseError: z.string().optional(),
  createdAt: z.string(),
})

export const zAdStageState = z.object({
  inputHash: z.string().optional(),
  lastRunAt: z.string().optional(),
  lastError: z.string().optional(),
})

const AD_STAGES: string[] = Object.values(EAdStage)

/**
 * `Partial<Record<EAdStage, T>>` — zod 3's enum-keyed `z.record` demands every key, which the
 * doc never has, so key membership is enforced by refinement instead.
 */
const zStageRecord = <T extends z.ZodTypeAny>(value: T) =>
  z.record(z.string(), value).superRefine((record, ctx) => {
    for (const key of Object.keys(record)) {
      if (!AD_STAGES.includes(key)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Unknown ad stage: ${key}`, path: [key] })
      }
    }
  })

export const zAdProject = z.object({
  schemaVersion: z.literal(AD_PROJECT_SCHEMA_VERSION),
  docVersion: z.number().int().positive(),
  formatId: z.string().min(1),
  bindings: z.record(z.string(), zAdBinding),
  context: zAdContext,
  costCapUsd: z.number().nonnegative(),
  generationProfile: z.nativeEnum(EAdGenerationProfile).optional(),
  pacing: z.nativeEnum(EAdPacing).optional(),
  script: z.object({ lines: z.array(zAdScriptLine) }).optional(),
  speech: z.object({ lines: z.record(z.string(), zAdSpeechLine) }).optional(),
  shots: z.array(zAdShot).optional(),
  keyframes: z
    .object({
      anchorTakeId: z.string().optional(),
      takes: z.record(z.string(), z.array(zAdTake)),
      selected: z.record(z.string(), z.number().int().nonnegative()),
      approved: z.object({ at: z.string(), byUserId: z.string(), hash: z.string() }).optional(),
    })
    .optional(),
  plan: zAdPlan.optional(),
  video: z
    .object({
      takes: z.record(z.string(), z.array(zAdTake)),
      selected: z.record(z.string(), z.number().int().nonnegative()),
    })
    .optional(),
  traces: zStageRecord(z.array(zPromptTrace)).default({}),
  overrides: zStageRecord(z.string()).default({}),
  stages: zStageRecord(zAdStageState).default({}),
  spend: z.object({ usd: z.number().nonnegative() }),
  execution: z
    .object({
      id: z.string().min(1),
      stage: zAdStage,
      inputHash: z.string(),
      startedAt: z.string(),
      target: z.object({ kind: z.literal("shot"), runId: z.string().uuid(), stepId: z.string().min(1) }).optional(),
    })
    .optional(),
  submissions: z
    .array(
      z.object({
        id: z.string().min(1),
        executionId: z.string().min(1).optional(),
        stage: zAdStage,
        unitId: z.string().min(1),
        modelId: z.string().min(1),
        createdAt: z.string(),
        estimatedUsd: z.number().nonnegative().optional(),
        jobId: z.string().min(1).optional(),
        settled: z.boolean().optional(),
        target: z.object({ kind: z.literal("shot"), runId: z.string().uuid(), stepId: z.string().min(1) }).optional(),
      }),
    )
    .optional(),
  recipePlans: z.record(z.string(), zShotPlan).optional(),
  recipeRuns: z.record(z.string(), zShotRun).optional(),
  render: z
    .object({
      jobId: z.string().min(1),
      status: z.enum(["pending", "completed", "failed"]),
      url: z.string().optional(),
      error: z.string().optional(),
      dispatchedAt: z.string(),
      qa: zAdQaGrade.optional(),
      judge: zAdJudgeGrade.optional(),
    })
    .optional(),
  clonedFrom: z
    .object({ videoId: z.string().min(1), docVersion: z.number().int().positive(), at: z.string() })
    .optional(),
})

export type TAdProjectParsed = z.infer<typeof zAdProject>

// ───────────────────────────── constructors ─────────────────────────────

export const createEmptyAdProject = (params: {
  formatId: string
  bindings: Record<string, IAdBinding>
  context: IAdContext
  costCapUsd?: number
  generationProfile?: EAdGenerationProfile
}): IAdProject => ({
  schemaVersion: AD_PROJECT_SCHEMA_VERSION,
  docVersion: 1,
  formatId: params.formatId,
  bindings: params.bindings,
  context: params.context,
  costCapUsd: params.costCapUsd ?? AD_ENGINE_CONSTANTS.DEFAULT_COST_CAP_USD,
  ...(params.generationProfile ? { generationProfile: params.generationProfile } : {}),
  traces: {},
  overrides: {},
  stages: {},
  spend: { usd: 0 },
})

/**
 * Fork a doc onto another video: everything the operator curated (bindings, context, script,
 * shots, takes, selections, approval, traces, overrides, spend) rides along, but the clone is a
 * fresh CAS lineage (`docVersion` 1) and has no timeline/render — those stages wrote
 * `vm_Video.tracksJson` on the *source* row, which the new video does not have.
 */
export const cloneAdProject = (
  source: IAdProject,
  from: { videoId: string; at: string; costCapUsd?: number },
): IAdProject => {
  // Recipe plans and render runs bind to source-video assets, jobs, and budget lineage.
  // A fork may reuse curated inputs, but it must re-plan before executing a shot.
  const { render: _renderJob, recipePlans: _recipePlans, recipeRuns: _recipeRuns, ...copy } = structuredClone(source)
  const { [EAdStage.TIMELINE]: _timeline, [EAdStage.RENDER]: _render, ...stages } = copy.stages
  return {
    ...copy,
    docVersion: 1,
    stages,
    // Spend carries over, so a fork that has to fund rerolls needs its cap raised here — `init`
    // is the only other place a cap is set, and the clone never runs it.
    costCapUsd: from.costCapUsd ?? copy.costCapUsd,
    clonedFrom: { videoId: from.videoId, docVersion: source.docVersion, at: from.at },
  }
}

// ───────────────────────────── fleet view ─────────────────────────────

/**
 * One row of the `/admin/ad-engine` index — a flattened projection of `IAdProject` plus the
 * owning `vm_Video`/`vm_Team` columns. The doc itself is never shipped to the list: a fleet of
 * projects is hundreds of KB of takes and traces nobody renders in a table.
 */
export interface IAdProjectSummary {
  videoId: VideoId
  title: string
  teamId: TeamId | null
  teamName: string | null
  formatId: string
  docVersion: number
  spendUsd: number
  costCapUsd: number
  freshness: Record<EAdStage, EAdFreshness>
  /** Highest stage in pipeline order whose freshness is not EMPTY; null when nothing has run. */
  lastStage: EAdStage | null
  updatedAt: string
}

export interface IAdProjectListResult {
  projects: IAdProjectSummary[]
  total: number
}

/** One graded take the human should look at (VM-1179 review queue). URLs are signed for display. */
export interface IAdReviewItem {
  stage: EAdStage.KEYFRAME | EAdStage.GENERATE
  /** Shot id (keyframe) or clip id (generate). */
  unitId: string
  jobId: string
  takeIndex: number
  /** This take is the one that would render. */
  selected: boolean
  mediaUrl: string
  /** The bound product's packshot, when this unit shows the product. */
  packshotUrl?: string
  /** The identity anchor still, for keyframes other than the anchor itself. */
  anchorUrl?: string
  shotPrompt?: string
  qa: IAdQaGrade
  review: IAdQaReview
}

export interface IAdReviewProject {
  videoId: VideoId
  title: string
  teamName: string | null
  formatId: string
  items: IAdReviewItem[]
}

export interface IAdReviewQueue {
  projects: IAdReviewProject[]
  /** Items across `projects` — what the badge shows. */
  total: number
  /** Projects scanned for this page; older projects are not in the queue. */
  scannedProjects: number
}
